/**
 * incidentInboxService.ts — Servicio de bandejas operativas (Lote 4.2)
 *
 * Consume los endpoints:
 *  - GET /api/incidences/inbox/mine
 *  - GET /api/incidences/inbox/department
 *  - GET /api/incidences/inbox/zone
 *
 * Cada respuesta incluye `availableActions` calculados por el backend.
 */

import { apiClient } from '@/services/api-client'

export interface InboxAction {
  action: string
  label: string
  transitionTo: string | null
  // P13-V1C (R0-INC-05 RESUELTO): `requires` es un OBJETO (mapa campo→boolean) — la forma que el
  // backend produce en TODOS los consumidores. La declaración anterior (`string[]`) describía un
  // wire-shape que nunca existió y hacía que `.includes` reventara el render del diálogo. Los
  // consumidores usan el adaptador `requiresField` (tolerante al legado); NUNCA volver a string[].
  requires: Record<string, boolean>
  // P13-V1D (decisión congelada #8 — FRONTEND_ESCALATION_POLICY = ZERO): los targets de
  // escalación viajan como proyección del SERVIDOR (única autoridad de pares); el FE solo
  // renderiza `constraints.allowedTargetRoles`, jamás computa pares desde el rol local.
  constraints?: { allowedTargetRoles?: string[] }
}

export interface InboxIncident {
  id: number
  title: string
  description: string
  status: string
  priority: string
  category?: string
  resolution?: string | null
  closedAt?: string | null
  createdAt: string
  updatedAt: string
  reportedBy: number
  assignedDepartmentId?: number | null
  assignedTo?: number | null
  groupId?: number | null
  assignedDepartment?: { id: number; name: string; code?: string } | null
  assignedUser?: { id: number; name: string; email: string; role: string } | null
  reportedByUser?: { id: number; name: string; email: string } | null
  group?: { id: number; name: string } | null
  point?: { id: number; name: string; address?: string } | null
  availableActions: InboxAction[]
}

export interface InboxResponse {
  success: boolean
  data: InboxIncident[]
  count: number
  page: number
  limit: number
  totalPages: number
}

export interface InboxFilters {
  status?: string
  page?: number
  limit?: number
}

function buildParams(filters: InboxFilters = {}): string {
  const params = new URLSearchParams()
  if (filters.status) params.append('status', filters.status)
  if (filters.page) params.append('page', String(filters.page))
  if (filters.limit) params.append('limit', String(filters.limit))
  return params.toString()
}

/**
 * Strict envelope adapter. After a `skipUnwrap: true` request the server pagination envelope must be
 * intact; anything else (unwrapped array, null, non-object, or missing/mistyped fields) is a frontend
 * HTTP-contract failure and MUST throw, so IncidentInbox.vue enters its existing error state. Malformed
 * or incomplete data must never render as a successful empty inbox, and pagination is never fabricated.
 */
function toInboxResponse(payload: any): InboxResponse {
  const valid =
    !!payload && typeof payload === 'object' && !Array.isArray(payload) &&
    payload.success === true &&
    Array.isArray(payload.data) &&
    typeof payload.count === 'number' &&
    typeof payload.page === 'number' &&
    typeof payload.limit === 'number' &&
    typeof payload.totalPages === 'number'
  if (!valid) {
    throw new Error('Inbox response envelope is invalid or incomplete (skipUnwrap contract violated)')
  }
  return {
    success: true,
    data: payload.data,
    count: payload.count,
    page: payload.page,
    limit: payload.limit,
    totalPages: payload.totalPages,
  }
}

export const incidentInboxService = {
  /** Incidencias creadas por el usuario autenticado */
  async getMine(filters: InboxFilters = {}): Promise<InboxResponse> {
    const qs = buildParams(filters)
    const response = await apiClient.get(`/incidences/inbox/mine${qs ? '?' + qs : ''}`, { skipUnwrap: true } as any)
    return toInboxResponse(response.data)
  },

  /** Incidencias del departamento del usuario */
  async getDepartment(filters: InboxFilters & { departmentId?: number } = {}): Promise<InboxResponse> {
    const params = new URLSearchParams()
    if (filters.status) params.append('status', filters.status)
    if (filters.page) params.append('page', String(filters.page))
    if (filters.limit) params.append('limit', String(filters.limit))
    if (filters.departmentId) params.append('departmentId', String(filters.departmentId))
    const qs = params.toString()
    const response = await apiClient.get(`/incidences/inbox/department${qs ? '?' + qs : ''}`, { skipUnwrap: true } as any)
    return toInboxResponse(response.data)
  },

  /** Incidencias de la zona (field_supervisor scope) */
  async getZone(filters: InboxFilters = {}): Promise<InboxResponse> {
    const qs = buildParams(filters)
    const response = await apiClient.get(`/incidences/inbox/zone${qs ? '?' + qs : ''}`, { skipUnwrap: true } as any)
    return toInboxResponse(response.data)
  },

  /**
   * P13-G-DEMO-PREQ-DEF01 — Bandeja OPERATIVA de la agencia asignada.
   *
   * Consume el listado CANÓNICO `GET /api/incidences`. La partición para `agency_employee` la deriva
   * el SERVIDOR y sólo el servidor: `resolveIncidenceReadScope` resuelve
   * POLICY_AGENCY_EMPLOYEE_ASSIGNED_POINT y aplica `pointId = <point asignado>` MÁS el cinturón
   * estricto `administrationId = <administración del actor>` como predicado SQL, con el point leído
   * de la `EmployeeAssignment` vigente. Por eso esta lectura NO envía selector de autoridad alguno:
   * pointId/agencyId/administrationId/reportedBy/groupId jamás salen de aquí — sólo viajan los
   * filtros puramente REDUCTORES que `buildParams` conoce (status/page/limit).
   *
   * Diferencia con `getMine`, y razón de existir: `getMine` está cerrada sobre `reportedBy = actor`,
   * así que estructuralmente no puede mostrar una incidencia que la empleada NO reportó. El Scenario B
   * exige exactamente eso — el reporter es un `route_operator` y la validadora es la empleada de la
   * agencia — por lo que la bandeja operativa debe leer por PUNTO, no por autoría.
   */
  async getAgency(filters: InboxFilters = {}): Promise<InboxResponse> {
    const qs = buildParams(filters)
    const response = await apiClient.get(`/incidences${qs ? '?' + qs : ''}`, { skipUnwrap: true } as any)
    return toInboxResponse(response.data)
  },

  /** Ejecutar acción del state machine sobre una incidencia */
  async executeAction(
    incidentId: number,
    action: string,
    payload: { comment?: string; resolution?: string } = {}
  ): Promise<any> {
    const response = await apiClient.put(`/incidences/${incidentId}/status`, {
      action,
      ...payload
    })
    return response.data
  },

  // ─── Endpoints canónicos del Lote 4.3 ─────────────────────

  /** T6/T7: Validar resolución (aceptar o rechazar) */
  async validateResolution(
    incidentId: number,
    accept: boolean,
    comment?: string
  ): Promise<any> {
    const response = await apiClient.post(`/incidences/${incidentId}/validate-resolution`, {
      accept,
      ...(comment ? { comment } : {}),
    })
    return response.data
  },

  /** T8: Reabrir incidencia */
  async reopen(incidentId: number, comment: string): Promise<any> {
    const response = await apiClient.post(`/incidences/${incidentId}/reopen`, {
      comment,
    })
    return response.data
  },

  /**
   * Historial enriquecido con entry_type y metadata.
   *
   * El interceptor por defecto ya desenvolvió `{ success, data }` (api-client.ts:96-97), así que
   * `response.data` ES la línea de tiempo. El `response.data?.data || []` anterior leía `.data` sobre
   * un array y devolvía SIEMPRE una línea de tiempo vacía — el mismo mecanismo que dejó sin
   * clasificaciones el intake W4. Una historia vacía es legítima; una forma imposible, no.
   */
  async getHistory(incidentId: number): Promise<HistoryEntry[]> {
    const response = await apiClient.get(`/incidences/${incidentId}/history`)
    const timeline = response.data
    if (!Array.isArray(timeline)) {
      throw new Error(`INCIDENT_HISTORY_SHAPE_INVALID: expected an array after the default unwrap, received ${timeline === null ? 'null' : typeof timeline}`)
    }
    return timeline
  },

  // ─── Endpoints canónicos del Lote 4.4: Escalamiento ──────

  /** Escalar incidencia a un rol superior */
  async escalateIncident(
    incidentId: number,
    toRole: string,
    reason: string
  ): Promise<any> {
    const response = await apiClient.post(`/incidences/${incidentId}/escalate`, {
      toRole,
      reason,
    })
    return response.data
  },

  /** Bandeja de escalaciones activas dirigidas al rol del usuario */
  async getEscalated(filters: InboxFilters = {}): Promise<InboxResponse> {
    const qs = buildParams(filters)
    const response = await apiClient.get(`/incidences/inbox/escalated${qs ? '?' + qs : ''}`, { skipUnwrap: true } as any)
    return toInboxResponse(response.data)
  },

  /** Reconocer una escalación */
  async acknowledgeEscalation(
    incidentId: number,
    escalationId: number
  ): Promise<any> {
    const response = await apiClient.post(
      `/incidences/${incidentId}/escalations/${escalationId}/acknowledge`
    )
    return response.data
  },
}

// ─── Tipos adicionales para Lote 4.3 ─────────────────────

export interface HistoryEntry {
  id: number
  oldStatus: string | null
  newStatus: string
  entryType: string
  comment: string | null
  metadata: Record<string, any> | null
  changedBy: number
  user: { id: number; name: string; username: string; role: string } | null
  createdAt: string
}

export interface EscalationInfo {
  id: number
  fromUserId: number
  fromUser: { id: number; name: string; email: string; role: string } | null
  toRole: string
  reason: string
  level: number
  createdAt: string
}
